import Box from '@mui/material/Box';
import {alpha, styled} from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import {visuallyHidden} from '@mui/utils';
import PropTypes from 'prop-types';
import HeaderCheckBox from './HeaderCheckBox';

const StyledTableHeaderRow = styled(TableRow)(({theme}) => {
  return {
    backgroundColor: alpha(theme.palette.secondary.main, 0.02),
    borderBottom: `2px solid ${theme.palette.divider}`,
    height: 50,

    '& th': {
      fontSize: 14,
      textTransform: 'uppercase',
      letterSpacing: '1px', // Increased spacing to keep the "Machined" feel at 14px
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap',
      transition: 'color 0.2s ease-in-out',
      verticalAlign: 'middle',
      padding: '0 10px',

      '&:first-of-type': {
        paddingLeft: 20,
      },
      '&:last-of-type': {
        paddingRight: 20,
      },
    },

    // Style for the Sort Icon to match Yaman Gold
    '& .MuiTableSortLabel-root.Mui-active': {
      color: theme.palette.primary.dark, // Yaman Gold
      '& .MuiTableSortLabel-icon': {
        color: `${theme.palette.primary.dark} !important`,
      },
    },
    '& .MuiTableSortLabel-root:hover': {
      color: theme.palette.primary.dark,
    },
  };
});

const TableHeader = ({
  tableSpec,
  tableParams,
  checkedRows,
  setCheckedRows,
  onRequestSort,
  dataColumns = [], // Default to empty array
  rows = [],
  totalRows = 0,
}) => {
  const {sortOrder, sortColumn} = tableParams;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <StyledTableHeaderRow>
        {tableSpec.checkboxselection && (
          <TableCell
            padding='checkbox'
            sx={{
              verticalAlign: 'middle',
            }}
          >
            <HeaderCheckBox
              checkedRows={checkedRows}
              setCheckedRows={setCheckedRows}
              idfieldname={tableSpec.idfieldname}
              rows={rows}
              totalRows={totalRows}
            />
          </TableCell>
        )}

        {dataColumns.map((column) => {
          if (!column.show) return null;

          const fieldSpec = tableSpec.fields.find(
            (f) => f.name === column.name,
          );

          const isSorted = sortColumn === column.name;

          return (
            <TableCell
              key={column.name}
              align={fieldSpec?.alignlabel || 'left'}
              sortDirection={isSorted ? sortOrder : false}
              className={isSorted ? 'Mui-active' : ''}
              sx={{
                height: 'inherit',
                verticalAlign: 'middle',
              }}
            >
              <TableSortLabel
                active={isSorted}
                direction={isSorted ? sortOrder : 'asc'}
                onClick={createSortHandler(column.name)}
              >
                {column.label}
                {isSorted ? (
                  <Box component='span' sx={visuallyHidden}>
                    {sortOrder === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </StyledTableHeaderRow>
    </TableHead>
  );
};

export default TableHeader;

TableHeader.propTypes = {
  tableSpec: PropTypes.shape({
    checkboxselection: PropTypes.bool,
    idfieldname: PropTypes.string,
    fields: PropTypes.array.isRequired,
  }).isRequired,

  tableParams: PropTypes.shape({
    sortOrder: PropTypes.oneOf(['asc', 'desc']).isRequired,
    sortColumn: PropTypes.string.isRequired,
  }).isRequired,

  checkedRows: PropTypes.array,
  setCheckedRows: PropTypes.func,
  onRequestSort: PropTypes.func.isRequired,
  dataColumns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
      show: PropTypes.bool,
    }),
  ),

  rows: PropTypes.array,
  totalRows: PropTypes.number,
};
